import { useEffect, useState } from "react";
import {
    Alert,
    Card,
    CardContent,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/Buttons";
import { changePassword, getMe, updateMe } from "../api";

/**
 * Profile — SCAFFOLD.
 *
 * Loads the signed-in user and saves the editable fields. Password change is
 * wired to the API but has no client-side confirm field yet.
 *
 * TODO: confirm-password field, avatar upload, success toasts.
 */
export default function Profile() {
    const [me, setMe] = useState(null);
    const [form, setForm] = useState({});
    const [passwords, setPasswords] = useState({
        currentPassword: "",
        newPassword: "",
    });
    const [message, setMessage] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await getMe();
                setMe(data);
                setForm({
                    firstName: data.first_name || "",
                    lastName: data.last_name || "",
                    mobileNumber: data.mobile_number || "",
                    designation: data.designation || "",
                    department: data.department || "",
                });
            } catch (error) {
                console.error("Failed to load profile", { error });
            }
        })();
    }, []);

    const set = (key) => (e) =>
        setForm((prev) => ({ ...prev, [key]: e.target.value }));

    async function handleSave() {
        setSaving(true);
        setMessage(null);
        try {
            const { data } = await updateMe(form);
            setMe(data);
            setMessage({ severity: "success", text: "Profile updated" });
        } catch (err) {
            setMessage({
                severity: "error",
                text: err.response?.data?.error || "Could not save",
            });
        } finally {
            setSaving(false);
        }
    }

    async function handlePasswordChange() {
        setMessage(null);
        try {
            const { data } = await changePassword(passwords);
            setMessage({ severity: "success", text: data.message });
            setPasswords({ currentPassword: "", newPassword: "" });
        } catch (err) {
            setMessage({
                severity: "error",
                text: err.response?.data?.error || "Could not change password",
            });
        }
    }

    return (
        <Stack>
            <PageHeader
                title="My Profile"
                subtitle={me ? `${me.email} · ${me.role_name}` : ""}
            />

            {message && (
                <Alert severity={message.severity} sx={{ mb: 2 }}>
                    {message.text}
                </Alert>
            )}

            <Stack sx={{ gap: 3 }}>
                <Card>
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            Details
                        </Typography>
                        <Stack sx={{ gap: 2, maxWidth: 480 }}>
                            <TextField
                                label="First name"
                                value={form.firstName || ""}
                                onChange={set("firstName")}
                            />
                            <TextField
                                label="Last name"
                                value={form.lastName || ""}
                                onChange={set("lastName")}
                            />
                            <TextField
                                label="Mobile number"
                                value={form.mobileNumber || ""}
                                onChange={set("mobileNumber")}
                            />
                            <TextField
                                label="Designation"
                                value={form.designation || ""}
                                onChange={set("designation")}
                            />
                            <TextField
                                label="Department"
                                value={form.department || ""}
                                onChange={set("department")}
                            />
                            <Button
                                variant="contained"
                                color="primary"
                                loading={saving}
                                onClick={handleSave}
                                sx={{ alignSelf: "flex-start" }}
                            >
                                Save changes
                            </Button>
                        </Stack>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            Change password
                        </Typography>
                        <Stack sx={{ gap: 2, maxWidth: 480 }}>
                            <TextField
                                label="Current password"
                                type="password"
                                value={passwords.currentPassword}
                                onChange={(e) =>
                                    setPasswords((p) => ({
                                        ...p,
                                        currentPassword: e.target.value,
                                    }))
                                }
                            />
                            <TextField
                                label="New password"
                                type="password"
                                value={passwords.newPassword}
                                onChange={(e) =>
                                    setPasswords((p) => ({
                                        ...p,
                                        newPassword: e.target.value,
                                    }))
                                }
                            />
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={handlePasswordChange}
                                sx={{ alignSelf: "flex-start" }}
                            >
                                Update password
                            </Button>
                        </Stack>
                    </CardContent>
                </Card>
            </Stack>
        </Stack>
    );
}
